import classNames from 'classnames'
import {
  cloneElement,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react'

import { IS_PROD } from '../../constants'
import {
  DocumentIcon,
  DownloadIcon,
  IconComponent,
  IconProps,
  isValidIconElement,
  PdfIcon,
  WarningIcon,
  WordIcon,
} from '../icon/icon'
import { IconButton, IconButtonProps } from '../icon-button/icon-button'
import { Spinner } from '../spinner/spinner'
import { ensurePropOf } from '../utilities'
import { assignSubComponents, isValidLinkElement } from '../utilities/internal'
import { Slot, Slottable } from '../utilities/slot/slot'
import { FileContext } from './context'
import styles from './file.module.scss'
import FileContent from './file-content'
import FileHelpText from './file-help-text'
import FileLabel from './file-label'
import { useFileProvider } from './use-file'

export type FileElement = HTMLDivElement
export const FileVariants = ['neutral', 'pdf', 'doc'] as const
export type FileVariant = (typeof FileVariants)[number]

export interface FileProps extends HTMLAttributes<FileElement> {
  children: ReactNode
  loading?: boolean
  variant?: FileVariant
  selected?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  disabled?: boolean
  error?: boolean
  asChild?: boolean
}

const fileIconMapping: Record<FileVariant, IconComponent> = {
  neutral: DocumentIcon,
  pdf: PdfIcon,
  doc: WordIcon,
}

const FileRoot = forwardRef<FileElement, FileProps>(function File(props, forwardedRef) {
  const {
    className,
    children,
    leftIcon,
    rightIcon = <DownloadIcon />,
    error: errorProp,
    selected: selectedProp,
    loading: loadingProp,
    disabled: disabledProp,
    variant: variantProp,
    asChild: asChildProp,
    ...rest
  } = props

  const context = useFileProvider(props)
  const { loading, disabled, selected, error } = context

  const asChild = asChildProp && isValidElement(children)
  const isLink = asChild && isValidLinkElement(children)

  const variant = ensurePropOf<FileVariant>(FileVariants, variantProp, 'neutral')
  const clickable = !error && (!!props.onClick || isLink)
  const isButtonRole = clickable && !isLink

  const isLeftIconValid = leftIcon && isValidElement<IconProps>(leftIcon)
  if (isLeftIconValid && variant !== 'neutral' && !IS_PROD) {
    console.warn('File: leftIcon is not supported for non-neutral variants')
  }

  const IconVariant = fileIconMapping[variant]
  const Icon = error ? WarningIcon : IconVariant

  const classes = classNames(
    styles.root,
    {
      [styles[`variant${variant}`]]: !!variant,
      [styles.clickable]: clickable,
      [styles.selected]: selected,
      [styles.disabled]: disabled,
      [styles.nointeractive]: disabled || loading,
      [styles.error]: error,
    },
    className
  )

  const iconProps: Partial<IconProps> = { size: 'sm' }
  const iconButtonProps: Partial<IconButtonProps> = { size: 'sm', kind: 'plain' }

  function renderRightIcon() {
    if (loading) {
      return <Spinner className={styles.rightIcon} {...iconProps} />
    }
    if (!isValidElement(rightIcon)) {
      return rightIcon
    }
    // Icon
    if (isValidIconElement(rightIcon)) {
      return cloneElement(rightIcon, { className: styles.rightIcon, ...iconProps })
    }
    // IconButton
    if (rightIcon.type === IconButton) {
      return cloneElement(rightIcon, { className: styles.rightIcon, ...iconButtonProps })
    }
    return cloneElement(rightIcon as ReactElement, { className: styles.rightIcon })
  }

  const Component = asChild ? Slot : 'div'

  return (
    <FileContext.Provider value={context}>
      <Component
        aria-disabled={disabled || undefined}
        aria-labelledby={context.id}
        ref={forwardedRef}
        role={isButtonRole ? 'button' : undefined}
        tabIndex={isButtonRole ? 0 : undefined}
        {...rest}
        className={classes}
      >
        {isLeftIconValid && variant === 'neutral' ? (
          cloneElement(leftIcon, {
            ...iconProps,
            className: classNames(styles.icon, leftIcon.props?.className),
          })
        ) : (
          <Icon className={styles.icon} {...iconProps} />
        )}

        <Slottable>{children}</Slottable>

        {renderRightIcon()}
      </Component>
    </FileContext.Provider>
  )
})

export const File = assignSubComponents('File', FileRoot, {
  Content: FileContent,
  HelpText: FileHelpText,
  Label: FileLabel,
})

export * from './file-content'
export * from './file-help-text'
export * from './file-label'
